import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loadUser } from "../../redux/actions/user";
import { server } from "../../server";
import axios from "axios";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";

const STATUS_LABELS = {
  none: null,
  pending: "Pending verification",
  verified: "Verified veteran",
  rejected: "Proof declined – you can submit new proof below",
};

const ProfileVeteran = () => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [proofFile, setProofFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didClickSaveRef = useRef(false);

  const status = user?.veteranVerificationStatus || "none";
  const hasProof = !!user?.veteranProofImage;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
      toast.error(`Image must be under ${IMAGE_UPLOAD_MAX_MB}MB.`);
      e.target.value = "";
      return;
    }
    setProofFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!didClickSaveRef.current) return;
    didClickSaveRef.current = false;
    if (!proofFile) {
      toast.error("Please select a proof image (e.g. veteran ID or documentation).");
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("proof", proofFile);
      const { data } = await axios.put(`${server}/user/veteran-claim`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      toast.success(data.message || "Veteran proof submitted for verification.");
      setProofFile(null);
      e.target.reset();
      dispatch(loadUser());
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit proof.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardClass = "rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8";
  const labelClasses = "block text-sm font-medium text-slate-600";
  const primaryButtonClasses =
    "inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:opacity-60";

  return (
    <section className={cardClass}>
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Veteran discount</h2>
        <p className="text-sm text-slate-500 mt-1">
          If you are a veteran, you can receive certain discounts. Declare your status and upload proof; an admin will verify your account. Veteran status is only visible to administrators.
        </p>
      </header>

      {status !== "none" && (
        <div className="mb-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <p className="text-sm font-medium text-slate-700">
            Status: {STATUS_LABELS[status] || status}
          </p>
          {hasProof && status !== "rejected" && (
            <p className="text-xs text-slate-500 mt-1">Proof image on file (visible to admin only).</p>
          )}
        </div>
      )}

      {(status === "none" || status === "rejected") && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClasses}>Upload proof of veteran status</label>
            <p className="text-xs text-slate-500 mb-2">
              e.g. veteran ID, service document, or official letter. Max {IMAGE_UPLOAD_MAX_MB}MB. Image files only.
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-[#38513b] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#2f4232]"
            />
            {proofFile && (
              <p className="text-xs text-slate-600 mt-1">Selected: {proofFile.name}</p>
            )}
          </div>
          <button
            type="submit"
            className={primaryButtonClasses}
            disabled={!proofFile || isSubmitting}
            onMouseDown={() => {
              didClickSaveRef.current = true;
            }}
          >
            {isSubmitting ? "Submitting…" : "Submit for verification"}
          </button>
        </form>
      )}

      {status === "pending" && (
        <p className="text-sm text-slate-600">
          Your proof has been submitted. An administrator will review it shortly. You will receive veteran discounts once verified.
        </p>
      )}

      {status === "verified" && (
        <p className="text-sm text-emerald-700 font-medium">
          You are verified as a veteran. You are eligible for veteran discounts on the platform.
        </p>
      )}
    </section>
  );
};

export default ProfileVeteran;
